import { ROLES } from "./Roles";
import type { Role } from "./Roles";
import { CAPABILITIES } from "./Capabilities";
import type { Capability } from "./Capabilities";

/**
 * THE registry of which screens each persona may see.
 *
 * <p>Two consumers read it and nothing else decides: the side nav (which items to list) and
 * {@code PermissionGuard} (whether a routed screen may render at all). Before this existed the two
 * answers were different — the nav had per-item {@code show:} flags while routing had only
 * {@code AuthGuard}, which checks nothing but "is someone logged in". So a trainer who typed
 * {@code /app/exercises} got the page, and every request it fired came back 403.
 *
 * <p><b>THIS IS PRESENTATION, NOT ENFORCEMENT.</b> The server is the security boundary: it is
 * default-deny and re-decides every request from the caller's own principal, regardless of what the
 * client believes. Nothing here grants anything. Hiding a nav item or redirecting away from a route
 * only spares the user a screen whose every call would be refused — and the app-wide 403
 * {@code AccessDeniedScreen} (see {@code AccessDeniedContext}) remains the safety net for whatever
 * this registry gets wrong. Never treat a hidden nav item as a control.
 */

/**
 * What a screen asks of its viewer.
 *
 * <p>Three shapes, in the order they are checked:
 * <ul>
 *   <li>{@code roles} — any ONE of these authorities admits the viewer.</li>
 *   <li>{@code capability} — a delegated MANAGER grant admits the viewer. A company ADMIN reports
 *       holding every capability (the server says so), so a capability alone also admits an admin;
 *       that is why entries here ask for the capability rather than {@code ADMIN || capability}.</li>
 *   <li>neither — any authenticated staff member. Login already rejects anyone outside
 *       {@code ADMIN_APP_ROLES}, so "no requirement" means "everyone who got in".</li>
 * </ul>
 */
export interface ScreenAccess {
  /** The route this describes, without a trailing slash. Detail routes inherit it by prefix. */
  path: string;
  /** Any one of these authorities admits the viewer. Omitted → not role-gated. */
  roles?: readonly Role[];
  /** This delegated capability admits the viewer. Omitted → not capability-gated. */
  capability?: Capability;
  /**
   * Registered so the route resolves, but never offered as a landing target (and not a nav item).
   * Used for detail/alias routes and for screens that are switched off in the nav.
   */
  hidden?: boolean;
  /**
   * A screen that belongs to the PERSON, not the tenant — reachable by anyone who can log in, and
   * exempt from the two company-less persona allow-lists below.
   *
   * <p>Those Sets historically governed only which nav items rendered; a super-admin could still
   * reach a page not on the list. Now that they gate routing too, a self-scoped screen would become
   * unreachable — and `/app/setting` is where every persona edits their own name, phone, email and
   * password (it also carries the super-admin's API-keys section). Redirecting them off it would be
   * a regression in a live click path: the avatar menu links straight there
   * (`componenets/nav/ProfileMenu.js`).
   */
  selfScoped?: boolean;
}

/**
 * The ONLY nav items / screens a SYSTEM_ADMIN may reach. A super-admin has no company context, so
 * every company-scoped page (clients, trainers, tasks, food, forms, settings, …) is off-limits —
 * they operate on global/system resources only, and reach a specific company via impersonation.
 *
 * <p>This is a hard allow-list applied on top of each screen's own {@link ScreenAccess}. It matters
 * because a super-admin account can ALSO carry ADMIN/TRAINER authorities (e.g. admin@gmail.com has
 * all roles), which would otherwise light up company-scoped screens. The allow-list guarantees none
 * of them leak in for a super-admin.
 *
 * <p>Deliberately NOT dissolved into the role/capability model above: SYSTEM_ADMIN is a fixed-nav,
 * company-less persona, not a data-driven one, and the Set is the thing the existing SideNav tests
 * lock down.
 */
export const SYSTEM_ADMIN_ALLOWED_PATHS: ReadonlySet<string> = new Set([
  "/app/dashboard", // system-aware overview (all-companies breakdown)
  "/app/templates", // SYSTEM/global workout templates (→ /system/template)
  "/app/exercises", // SYSTEM/global exercises (→ /system/exercise)
  "/app/import-users",
  "/app/import-template",
  "/app/food-dictionary",
  "/app/marketplace",
  "/app/partnerships",
  "/app/coupons",
  "/app/admin-messages",
  "/app/build-console",
  "/app/audit-log",
  "/app/companies",
  "/app/administrators",
]);

/**
 * A COMPANY_GROUP_ADMIN is a company-less persona (like a super-admin) that only manages a curated
 * set of companies via impersonation. In the Admin UI they see ONLY the Dashboard — which is their
 * companies overview (scoped to their authorized companies) with the impersonation drill-in — plus
 * their OWN content libraries. They do NOT get the SYSTEM_ADMIN-only Companies management page, nor
 * anything company-scoped or platform tooling. Same rationale as the Set above: a hard allow-list
 * for a fixed-nav persona, applied on top of each screen's own requirement.
 */
export const COMPANY_GROUP_ADMIN_ALLOWED_PATHS: ReadonlySet<string> = new Set([
  "/app/dashboard",
  // The group admin's OWN content libraries (owner-scoped, assigned only to authorized companies).
  // These reuse the Templates/Exercises screens but hit the /groupAdmin/* endpoints.
  "/app/templates",
  "/app/exercises",
]);

/**
 * Every routed {@code /app} screen, in nav order (which is also the order
 * {@link firstPermittedPath} walks when picking where to send someone).
 *
 * <p>Each entry mirrors what the side nav used to express as a per-item {@code show:} flag. The
 * frequent "everyone except a super-admin" case is expressed as *no requirement*: the SYSTEM_ADMIN
 * allow-list above already excludes a super-admin from anything it does not list, so restating
 * {@code !isSuperAdmin} per entry would be redundant, not safer.
 *
 * <p>Note what is deliberately NOT here: feature flags and runtime state (the marketplace being
 * open, the nutrition-V2 flag). Those are not permissions and stay with the nav item.
 */
export const NAVIGATION: readonly ScreenAccess[] = [
  // ── ראשי ──────────────────────────────────────────────────────────────────────────────────────
  // Not a plain MANAGER: the dashboard is the admin/trainer/group-admin overview, and a manager who
  // is not also a trainer has no coach dashboard to show. (Unchanged from the nav's `show` flag.)
  {
    path: "/app/dashboard",
    roles: [ROLES.SYSTEM_ADMIN, ROLES.COMPANY_GROUP_ADMIN, ROLES.ADMIN, ROLES.TRAINER],
  },

  // ── ניהול משתמשים ────────────────────────────────────────────────────────────────────────────
  { path: "/app/clients" },
  // Company-scoped (a company's own trainers). Open to a MANAGER granted MANAGE_TRAINERS, who sees
  // only the trainers in their scope (enforced server-side), and to a company ADMIN — who reports
  // holding every capability, so the capability alone covers them.
  { path: "/app/trainers", capability: CAPABILITIES.MANAGE_TRAINERS },
  // ROLE-gated on purpose, never capability-gated: a manager must never mint a peer or widen their
  // own grant, so no capability may ever unlock this screen.
  { path: "/app/managers", roles: [ROLES.ADMIN] },
  // Switched off in the nav (the screen exists but is not offered); still routable.
  { path: "/app/alerts", hidden: true },
  // Mirrors AdminScheduledNotificationController: SYSTEM_ADMIN/ADMIN/TRAINER, or the grant. The
  // super-admin half is carried by the allow-list rather than restated here.
  {
    path: "/app/notifications",
    roles: [ROLES.ADMIN, ROLES.TRAINER],
    capability: CAPABILITIES.MANAGE_NOTIFICATIONS,
  },
  // Trainer-scoped task list.
  { path: "/app/tasks", roles: [ROLES.TRAINER] },
  { path: "/app/check-ins" },
  { path: "/app/import-users", roles: [ROLES.SYSTEM_ADMIN] },

  // ── תוכן ואימונים ────────────────────────────────────────────────────────────────────────────
  // Company admins manage their own templates/exercises; super-admin manages the SYSTEM/global
  // library; a group admin manages its OWN (the services route each to the right controller).
  // Training content is a TRAINER's job. Both entries now mirror BaseTemplateController /
  // BaseExerciseController exactly — "hasAnyAuthority('SYSTEM_ADMIN','ADMIN','TRAINER') or
  // @caps.can('MANAGE_…')" — where before the nav withheld from trainers a screen the server had
  // always let them open by URL.
  {
    path: "/app/templates",
    roles: [ROLES.ADMIN, ROLES.SYSTEM_ADMIN, ROLES.COMPANY_GROUP_ADMIN, ROLES.TRAINER],
    capability: CAPABILITIES.MANAGE_TEMPLATES,
  },
  { path: "/app/program_renew" },
  {
    path: "/app/exercises",
    roles: [ROLES.ADMIN, ROLES.SYSTEM_ADMIN, ROLES.COMPANY_GROUP_ADMIN, ROLES.TRAINER],
    capability: CAPABILITIES.MANAGE_EXERCISES,
  },
  { path: "/app/import-template", roles: [ROLES.SYSTEM_ADMIN] },

  // ── תזונה ומשאבים ────────────────────────────────────────────────────────────────────────────
  // CONTENT LIBRARIES — role for the incumbents, capability for a delegate.
  //
  // Each entry mirrors its server expression exactly:
  //   hasAnyAuthority('ADMIN','TRAINER') or @caps.can('MANAGE_X')
  // and canAccessPath resolves roles OR capability, so the two read as the same rule. A company
  // ADMIN reports holding every capability, so either half would admit them; the role is listed
  // anyway, because TRAINER holds no capabilities and would otherwise lose screens they have always
  // had.
  //
  // The two company-less personas need no mention: none of these paths is in
  // SYSTEM_ADMIN_ALLOWED_PATHS or COMPANY_GROUP_ADMIN_ALLOWED_PATHS, and those allow-lists are
  // consulted first.
  //
  // Note what is NOT gated this way: a manager assigns a form and applies a menu from inside a
  // client's page, guarded by the trainee's own scope-aware check rather than by these permissions.
  // So /app/clients stays open to a manager who holds no content grant at all.
  // The catalog replaced the legacy /app/food screen, and inherits its audience: whoever could manage
  // foods before can manage their company's foods now. A system admin also reaches it, and is the only
  // one who sees the scope/company filters or can touch a system food.
  { path: "/app/food-dictionary", roles: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.TRAINER], capability: CAPABILITIES.MANAGE_FOOD },
  // The V2 nutrition libraries. They used to be one ungated page reading a local mock store — "no
  // server calls at all yet, so there is nothing for a floor to mirror". They have a backend now
  // (AdminNutritionLibraryV2Controller), so they are gated, and each is its own page.
  //
  // Mirrors that controller EXACTLY: class-level `hasAnyAuthority('ADMIN','TRAINER')`, and no
  // capability expression anywhere on it. Declaring MANAGE_MENU_TEMPLATES here would be the very
  // failure this registry exists to prevent — a manager holding the grant would be offered the
  // screen and every call it fired would come back 403. See the note on /app/global-templates below.
  { path: "/app/menu-templates", roles: [ROLES.ADMIN, ROLES.TRAINER] },
  { path: "/app/saved-meals", roles: [ROLES.ADMIN, ROLES.TRAINER] },
  // Both old paths still resolve as redirects (routes.js): /app/nutrition-library?tab=… splits into
  // the two pages above. Registered so the redirect is reachable, hidden so it is never a landing
  // target or a nav row.
  { path: "/app/nutrition-library", roles: [ROLES.ADMIN, ROLES.TRAINER], hidden: true },
  {
    path: "/app/supplements",
    roles: [ROLES.ADMIN, ROLES.TRAINER],
    capability: CAPABILITIES.MANAGE_SUPPLEMENTS,
  },
  {
    path: "/app/company-measurements",
    roles: [ROLES.ADMIN],
    capability: CAPABILITIES.MANAGE_MEASUREMENTS,
  },
  // The V1 global-templates PAGE is gone; this path now only redirects to /app/menu-templates.
  //
  // NOTE — a real narrowing, recorded rather than hidden: this screen admitted a MANAGER holding
  // MANAGE_MENU_TEMPLATES, and its V2 replacement does not, because the V2 controller never grew a
  // @caps.can('MANAGE_MENU_TEMPLATES') clause. Keeping the capability here would only send such a
  // manager to a page that 403s. Restoring them is a server change, not a registry one.
  {
    path: "/app/global-templates",
    roles: [ROLES.ADMIN, ROLES.TRAINER],
    capability: CAPABILITIES.MANAGE_MENU_TEMPLATES,
    hidden: true,
  },
  { path: "/app/guides", roles: [ROLES.ADMIN], capability: CAPABILITIES.MANAGE_GUIDES },
  {
    path: "/app/recipes",
    roles: [ROLES.ADMIN],
    capability: CAPABILITIES.MANAGE_RECIPES,
  },
  { path: "/app/forms", roles: [ROLES.ADMIN, ROLES.TRAINER], capability: CAPABILITIES.MANAGE_FORMS },
  // SYSTEM_ADMIN always sees it (with a closed banner); for a company persona the nav item also
  // needs the marketplace to be open — runtime state, so it lives on the nav item, not here.
  { path: "/app/marketplace", roles: [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.TRAINER] },

  // ── מערכת ────────────────────────────────────────────────────────────────────────────────────
  { path: "/app/partnerships", roles: [ROLES.SYSTEM_ADMIN] },
  { path: "/app/coupons", roles: [ROLES.SYSTEM_ADMIN] },
  { path: "/app/admin-messages", roles: [ROLES.SYSTEM_ADMIN] },
  { path: "/app/build-console", roles: [ROLES.SYSTEM_ADMIN] },
  { path: "/app/audit-log", roles: [ROLES.SYSTEM_ADMIN] },
  { path: "/app/companies", roles: [ROLES.SYSTEM_ADMIN] },
  { path: "/app/administrators", roles: [ROLES.SYSTEM_ADMIN] },
  // Mirrors AdminAutomationFlowController: "hasAnyAuthority('ADMIN','SYSTEM_ADMIN') or
  // @caps.can('MANAGE_AUTOMATIONS')". Not part of the trainer baseline and never will be — an
  // automation acts on trainees on a schedule with nobody watching, so it is company machinery
  // rather than coaching, and stays an explicit checkbox.
  {
    path: "/app/automations",
    roles: [ROLES.ADMIN],
    capability: CAPABILITIES.MANAGE_AUTOMATIONS,
  },
  // Self-scoped: every persona edits their own profile here, and the avatar menu links to it.
  { path: "/app/setting", selfScoped: true },

  // ── routed but never listed in the nav (aliases and detail entry points whose own screen sits
  //    above them, so they need their own entry to resolve) ──────────────────────────────────────
  { path: "/app/automations-flow", roles: [ROLES.ADMIN], capability: CAPABILITIES.MANAGE_AUTOMATIONS, hidden: true },
  { path: "/app/automations-v2", roles: [ROLES.ADMIN], capability: CAPABILITIES.MANAGE_AUTOMATIONS, hidden: true },
];

/** The viewer a screen is being resolved for: the authorities they hold and their grants. */
export interface Viewer {
  roles: readonly Role[];
  can: (capability: Capability) => boolean;
}

/**
 * The auth surface {@link viewerFromAuth} reads. Structural on purpose: it accepts the real
 * {@code AuthContextValue} and equally a test double that only implements some of it — every member
 * is optional and a missing predicate reads as "does not hold that role", which is the fail-closed
 * answer.
 */
export interface AuthLike {
  isSuperAdmin?: () => boolean;
  isCompanyGroupAdmin?: () => boolean;
  isCompanyAdmin?: () => boolean;
  isManager?: () => boolean;
  isTrainer?: () => boolean;
  can?: (capability: Capability) => boolean;
}

/** Adapt {@code useAuth()} to the {@link Viewer} shape this module works in. */
export function viewerFromAuth(auth: AuthLike | undefined | null): Viewer {
  const roles: Role[] = [];
  if (auth?.isSuperAdmin?.()) roles.push(ROLES.SYSTEM_ADMIN);
  if (auth?.isCompanyGroupAdmin?.()) roles.push(ROLES.COMPANY_GROUP_ADMIN);
  if (auth?.isCompanyAdmin?.()) roles.push(ROLES.ADMIN);
  if (auth?.isManager?.()) roles.push(ROLES.MANAGER);
  if (auth?.isTrainer?.()) roles.push(ROLES.TRAINER);
  // A caller that supplies no capability predicate grants nothing.
  const can = auth?.can;
  return { roles, can: can ? (c: Capability) => !!can(c) : () => false };
}

/**
 * The registry entry governing a pathname, or {@code undefined} when nothing covers it.
 *
 * <p>Matches the LONGEST registered path that the pathname either equals or sits under, so
 * {@code /app/clients/42/nutrition} inherits {@code /app/clients}. The boundary is a real path
 * segment ("/"), so {@code /app/food-dictionary} is never mistaken for a child of {@code /app/food}.
 */
export function resolveScreen(pathname: string): ScreenAccess | undefined {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  let best: ScreenAccess | undefined;
  for (const screen of NAVIGATION) {
    if (path === screen.path || path.startsWith(`${screen.path}/`)) {
      if (!best || screen.path.length > best.path.length) best = screen;
    }
  }
  return best;
}

/**
 * May this viewer see this screen?
 *
 * <p>Unregistered paths are allowed: this layer is presentation, and a route nobody wrote a rule
 * for must not become unreachable because of it — the server still decides.
 */
export function canAccessPath(pathname: string, viewer: Viewer): boolean {
  const screen = resolveScreen(pathname);
  if (!screen) return true;

  // Hard allow-lists for the two company-less, fixed-nav personas — applied on top of, and ahead
  // of, the screen's own requirement (an account can hold these roles alongside ADMIN/TRAINER).
  if (viewer.roles.includes(ROLES.SYSTEM_ADMIN) && !SYSTEM_ADMIN_ALLOWED_PATHS.has(screen.path)) {
    return false;
  }
  if (
    viewer.roles.includes(ROLES.COMPANY_GROUP_ADMIN) &&
    !COMPANY_GROUP_ADMIN_ALLOWED_PATHS.has(screen.path)
  ) {
    return false;
  }

  if (screen.roles?.some((role) => viewer.roles.includes(role))) return true;
  if (screen.capability && viewer.can(screen.capability)) return true;
  // No requirement at all → any authenticated staff member.
  return !screen.roles && !screen.capability;
}

/**
 * May this viewer ROUTE to this screen? Strictly wider than {@link canAccessPath}.
 *
 * <p>The two questions differ for self-scoped screens, and conflating them is a regression waiting to
 * happen. The persona allow-lists have always kept `/app/setting` out of a super-admin's *nav* — but
 * they could still open it, and they need to: it is where every persona edits their own name, phone,
 * email and password, the avatar menu links straight there
 * (`componenets/nav/ProfileMenu.js`), and it carries the super-admin's API-keys section.
 *
 * <p>So: the nav asks {@link canAccessPath}, the route guard asks this.
 */
export function canRouteTo(pathname: string, viewer: Viewer): boolean {
  const screen = resolveScreen(pathname);
  if (screen?.selfScoped) return true;
  return canAccessPath(pathname, viewer);
}

/**
 * The first screen this viewer may actually see, in nav order — where {@code PermissionGuard} sends
 * someone who landed on a screen that is not theirs. {@code undefined} when they may see nothing,
 * which login should already have prevented.
 */
export function firstPermittedPath(viewer: Viewer): string | undefined {
  return NAVIGATION.find((screen) => !screen.hidden && canAccessPath(screen.path, viewer))?.path;
}
